import React from 'react';
import { Package, Music, BookOpen, Gift, Check, Users, Sparkles, Award } from 'lucide-react';
import { Button } from './ui/button';

const DONORBOX_URL = 'https://thegardensofhope.org/donate-to-the-gardens-of-hope-donorbox/';

const TIERS = [
  {
    id: 'transformation',
    price: 60,
    priceLabel: '$60',
    name: 'Sponsor a Transformation',
    icon: Gift,
    accent: 'from-[#4B4E6D] to-[#2C2E43]',
    highlight: false,
    tagline: 'Change one life. Start where you are.',
    includes: [
      'All 3 Gasoline Workbooks',
      'Urbanation album (digital)',
      'Delivered to a recipient of your choice',
      'Tax-deductible via Gardens of Hope',
    ],
    donorboxNote: 'Select "Sponsor a Transformation" in the program dropdown.',
    cta: 'SPONSOR FOR $60',
  },
  {
    id: 'cohort',
    price: 1500,
    priceLabel: '$1,500',
    name: 'Sponsor a Cohort',
    icon: Users,
    accent: 'from-[#D4AF37] to-[#FFD700]',
    highlight: true,
    tagline: 'Fund an entire class of 12 through the program.',
    includes: [
      'Workbooks + Urbanation album for a full 12-person cohort',
      'Named recognition at the cohort\u2019s graduation ceremony',
      'Digital sponsor certificate',
      'Quarterly impact update with participant stories',
      'Tax-deductible via Gardens of Hope',
    ],
    donorboxNote: 'Select "Sponsor a Cohort" and note "Cohort Sponsorship" in the comments.',
    cta: 'SPONSOR A COHORT',
  },
  {
    id: 'ceremony',
    price: 5000,
    priceLabel: '$5,000',
    name: 'Sponsor a Ceremony',
    icon: Sparkles,
    accent: 'from-[#E63946] to-[#B22222]',
    highlight: false,
    tagline: 'Underwrite a full graduation ceremony end to end.',
    includes: [
      'Complete ceremony production (venue, meal, keepsakes)',
      'Sponsor logo displayed at the event',
      'Short thank-you video from participants',
      'Two invitations to attend the ceremony',
      'Featured recognition on our website',
      'Tax-deductible via Gardens of Hope',
    ],
    donorboxNote: 'Select "Sponsor a Ceremony" and add your organisation name in the comments.',
    cta: 'SPONSOR A CEREMONY',
  },
];

const TierCard = ({ tier }) => {
  const Icon = tier.icon;
  const openDonorbox = () => window.open(DONORBOX_URL, '_blank');

  return (
    <div
      className={`relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 ${
        tier.highlight ? 'border-[#D4AF37]' : 'border-[#D4AF37]/20'
      } rounded-3xl p-8 flex flex-col transition-all duration-300 hover:border-[#D4AF37] hover:transform hover:-translate-y-2`}
      data-testid={`tier-${tier.id}`}
    >
      {tier.highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] font-bold px-4 py-1 rounded-full text-xs tracking-wider">
          MOST IMPACT
        </div>
      )}

      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tier.accent} flex items-center justify-center mb-6`}>
        <Icon className="w-7 h-7 text-[#0a0a0a]" />
      </div>

      <h3 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
        {tier.name}
      </h3>
      <p className="text-gray-400 text-sm mb-6 min-h-[3rem]">{tier.tagline}</p>

      <div className="mb-6">
        <span
          className={`text-5xl md:text-6xl font-bold ${
            tier.highlight
              ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]'
              : 'text-white'
          }`}
          style={{ fontFamily: '"Bebas Neue", sans-serif' }}
        >
          {tier.priceLabel}
        </span>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {tier.includes.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <span className="text-gray-300 text-sm leading-snug">{item}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={openDonorbox}
        data-testid={`tier-cta-${tier.id}`}
        className={`w-full font-bold py-6 text-sm sm:text-base whitespace-normal leading-tight break-words transition-all duration-300 transform hover:scale-[1.02] ${
          tier.highlight
            ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37]'
            : 'bg-[#0a0a0a] border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0a]'
        }`}
      >
        {tier.cta}
      </Button>

      <p className="text-xs text-gray-500 mt-4 leading-relaxed">{tier.donorboxNote}</p>
    </div>
  );
};

const Sponsorship = () => {
  return (
    <section id="sponsor" className="relative py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <Award className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">CHOOSE YOUR IMPACT</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            THREE WAYS TO{' '}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              TRANSFORM LIVES
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Every dollar routes through Gardens of Hope, our 501(c)(3) partner.
            Pick the tier that fits your capacity. Every one of them saves lives.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>

        <div className="text-center bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/5 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-2xl p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-sm md:text-base text-gray-300">
            <span className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#D4AF37]" /> Trauma-informed curriculum</span>
            <span className="text-gray-600 hidden md:inline">|</span>
            <span className="flex items-center gap-2"><Music className="w-4 h-4 text-[#D4AF37]" /> Original healing music</span>
            <span className="text-gray-600 hidden md:inline">|</span>
            <span className="flex items-center gap-2"><Package className="w-4 h-4 text-[#D4AF37]" /> Fulfilled by our team</span>
          </div>
          <p className="text-gray-500 text-xs mt-4 max-w-2xl mx-auto">
            Custom or larger corporate / foundation sponsorships available.
            Contact us via the Prayer &amp; Support form or email through Gardens of Hope.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Sponsorship;
